/**
 * InstrumentLibrary.js
 * Source of truth for Instrument definitions, MIDI mappings, and Orchestration logic.
 * Aligned with Spitfire BBCO Discover standards.
 */

// Spitfire BBCO Professional Key Switches (Standardized)
// Aligned with C-2 (Note 0) Start

export const BBCO_KEYSWITCHES = {
    LONG: 0,
    SPICCATO: 1,
    PIZZICATO: 2,
    TREMOLO: 3,
    TENUTO: 4,
    MARCATO: 5,
    COL_LEGNO: 6,
    HARMONICS: 7,
    SUL_TASTO: 8,
    CON_SORD: 9,
    FLAUTANDO: 10,
    TRILL_MAJ2: 11,
    TRILL_MIN2: 12
};

// UACC (Universal Articulation Controller Channel) Standard Mapping
export const UACC_MAP = {
    LONG: 1,
    LEGATO: 20,
    STACCATO: 40,
    SPICCATO: 42,
    MARCATO: 52,
    PIZZICATO: 56,
    TREMOLO: 11,
    TRILL: 70,
    COL_LEGNO: 58,
    SUL_TASTO: 7,
    HARMONICS: 6,
    CON_SORD: 5,
    FLAUTANDO: 8
};

// Explicit mapping of Logical Tags to Keyswitches
const STRING_LOGIC_TAGS = {
    legato: BBCO_KEYSWITCHES.LONG,
    long: BBCO_KEYSWITCHES.LONG,
    sustain: BBCO_KEYSWITCHES.LONG,
    spiccato: BBCO_KEYSWITCHES.SPICCATO,
    staccato: BBCO_KEYSWITCHES.SPICCATO,
    short: BBCO_KEYSWITCHES.SPICCATO,
    pizzicato: BBCO_KEYSWITCHES.PIZZICATO,
    pluck: BBCO_KEYSWITCHES.PIZZICATO,
    tremolo: BBCO_KEYSWITCHES.TREMOLO,
    shimmer: BBCO_KEYSWITCHES.TREMOLO,
    marcato: BBCO_KEYSWITCHES.MARCATO,
    col_legno: BBCO_KEYSWITCHES.COL_LEGNO,
    harmonics: BBCO_KEYSWITCHES.HARMONICS,
    sul_tasto: BBCO_KEYSWITCHES.SUL_TASTO,
    con_sord: BBCO_KEYSWITCHES.CON_SORD,
    flautando: BBCO_KEYSWITCHES.FLAUTANDO,
    trill: BBCO_KEYSWITCHES.TRILL_MAJ2
};

const WIND_LOGIC_TAGS = {
    legato: BBCO_KEYSWITCHES.LONG,
    long: BBCO_KEYSWITCHES.LONG,
    sustain: BBCO_KEYSWITCHES.LONG,
    staccato: BBCO_KEYSWITCHES.SPICCATO,
    short: BBCO_KEYSWITCHES.SPICCATO,
    marcato: BBCO_KEYSWITCHES.MARCATO,
    tenuto: BBCO_KEYSWITCHES.TENUTO,
    trill: BBCO_KEYSWITCHES.TRILL_MAJ2,
    flutter: BBCO_KEYSWITCHES.TREMOLO
};

// Orchestral Hierarchy for Dynamic Orchestration
export const INSTRUMENT_HIERARCHY = {
    LEAD_GUITAR: 1,
    VIOLIN_1: 2,
    FLUTE: 3,
    TRUMPET: 4,
    PIANO_RH: 5,
    CLARINET: 6,
    OBOE: 7,
    SAXOPHONE: 8,
    SYNTH_LEAD: 9,
    VIOLIN_2: 10,
    VIOLA: 11,
    HORN: 12,
    CELLO: 13,
    RHYTHM_GUITAR: 14,
    TROMBONE: 15,
    BASSOON: 16,
    TUBA: 17,
    BASS: 18,
    PERCUSSION: 99
};

// Inlined Ranges to ensure standalone stability
// Widened ranges by +/- 5 semitones where appropriate to reduce clamping artifacts
const RANGES = {
    Violin: { min: 55, max: 97 }, // BBCSO: G3 - C#7
    Viola: { min: 48, max: 96 },
    Cello: { min: 36, max: 82 }, // BBCSO: C2 - Bb5
    Contrabass: { min: 24, max: 67 }, // Extended Low (Low C extension)
    Bass: { min: 24, max: 72 },
    Guitar: { min: 40, max: 96 }, // Solos go high
    Piano: { min: 21, max: 108 },
    Flute: { min: 60, max: 96 }, // BBCSO: C4 - C7
    Oboe: { min: 58, max: 96 },
    Clarinet: { min: 50, max: 96 },
    Bassoon: { min: 34, max: 76 },
    Trumpet: { min: 52, max: 90 },
    Horn: { min: 36, max: 80 }, // Pedal tones
    Trombone: { min: 36, max: 76 },
    Tuba: { min: 24, max: 60 },
    Timpani: { min: 36, max: 52 },
    Drums: { min: 0, max: 127 },
    Harp: { min: 24, max: 104 }, // BBCSO: C1 - G#7
    Harpsichord: { min: 36, max: 89 }, // C2 - F6 (Expanded to allow Bass role)
    Sitar: { min: 48, max: 88 },
    Koto: { min: 50, max: 90 },
    Duduk: { min: 55, max: 84 },
    SynthLead: { min: 36, max: 108 }, // Wide range for EDM
    SubBass: { min: 12, max: 60 } // Deep subs
};

export const INSTRUMENT_LIBRARY = [
    // --- GOTHIC / CHURCH ---
    {
        id: 'pipe_organ',
        name: 'Pipe Organ',
        tags: ['gothic', 'church', 'horror'],
        category: 'Orchestral',
        type: 'polyphonic',
        range: { min: 24, max: 96 }, // C1 - C7
        roles: ['harmony', 'bass', 'melody', 'pad'],
        midiChannel: 1,
        rank: 20,
        humanize_profile: 'mechanical',
        lung_capacity: 'infinite',
        mix_priority: 1,
        spectral_floor: 20, // Deep bass
        saturation_affinity: 0.8,
        preferredEngine: 'sampler',
        sampleMap: {
            default: {
                'C3': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/church_organ-mp3/C3.mp3',
                'C4': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/church_organ-mp3/C4.mp3',
                'C5': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/church_organ-mp3/C5.mp3',
                'C6': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/church_organ-mp3/C6.mp3'
            }
        },
        traits: { family: 'keys', section: 'full', orchestral: true }
    },
    {
        id: 'choir_aahs',
        name: 'Choir Aahs',
        tags: ['gothic', 'horror', 'epic'],
        category: 'Orchestral',
        type: 'polyphonic',
        range: { min: 48, max: 84 },
        roles: ['harmony', 'pad', 'melody'],
        midiChannel: 1,
        rank: 30,
        humanize_profile: 'expressive',
        lung_capacity: 'average',
        mix_priority: 2,
        spectral_floor: 100,
        saturation_affinity: 0.5,
        preferredEngine: 'sampler',
        sampleMap: {
            default: {
                'C4': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/choir_aahs-mp3/C4.mp3',
                'C5': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/choir_aahs-mp3/C5.mp3'
            }
        },
        traits: { family: 'voice', section: 'mid', orchestral: true }
    },
    {
        id: 'church_bell',
        name: 'Church Bell',
        tags: ['gothic', 'percussion', 'horror'],
        category: 'Orchestral',
        type: 'monophonic',
        range: { min: 48, max: 72 },
        roles: ['percussion', 'bell'],
        midiChannel: 1,
        rank: 95,
        humanize_profile: 'mechanical',
        lung_capacity: 'infinite',
        mix_priority: 3,
        spectral_floor: 150,
        saturation_affinity: 0.2,
        preferredEngine: 'sampler',
        sampleMap: {
            default: {
                'C5': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/tubular_bells-mp3/C5.mp3'
            }
        },
        traits: { family: 'percussion', section: 'high', orchestral: true }
    },

    // --- BAND / RHYTHM ---
    {
        id: 'drums',
        name: 'Drum Kit',
        preferredEngine: 'sampler',
        sampleMap: {
            // General MIDI Mapping (Tone.js C4=60, so C2=36)
            default: {
                'C2': 'https://raw.githubusercontent.com/Tonejs/audio/master/drum-samples/CR78/kick.mp3',   // 36
                'C#2': 'https://raw.githubusercontent.com/Tonejs/audio/master/drum-samples/CR78/rim.mp3',    // 37
                'D2': 'https://raw.githubusercontent.com/Tonejs/audio/master/drum-samples/CR78/snare.mp3',  // 38
                'E2': 'https://raw.githubusercontent.com/Tonejs/audio/master/drum-samples/CR78/snare.mp3',  // 40 (Electric Snare fallback)
                'F2': 'https://raw.githubusercontent.com/Tonejs/audio/master/drum-samples/CR78/tom1.mp3',   // 41 (Low Tom)
                'F#2': 'https://raw.githubusercontent.com/Tonejs/audio/master/drum-samples/CR78/hihat.mp3', // 42 (Closed Hat)
                'G2': 'https://raw.githubusercontent.com/Tonejs/audio/master/drum-samples/CR78/tom2.mp3',   // 43 (Low-Mid Tom)
                'A#2': 'https://raw.githubusercontent.com/Tonejs/audio/master/drum-samples/CR78/hihat.mp3', // 46 (Open Hat)
                'C3': 'https://raw.githubusercontent.com/Tonejs/audio/master/drum-samples/CR78/tom3.mp3'    // 48 (Hi-Mid Tom)
            }
        },
        category: 'Band',
        type: 'drum',
        range: RANGES.Drums,
        roles: ['percussion'],
        midiChannel: 10,
        rank: INSTRUMENT_HIERARCHY.PERCUSSION, // Legacy support

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 1, // Imperative
        spectral_floor: 0,
        saturation_affinity: 0.6,

        // Operation Virtuoso
        dynamic_profile: 'compressed', // Techno Kick
        envelope_bias: 'snap',
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['hit'],
            useDynamicsCC: false,
            traits: {
                timbre: 'neutral',
                attack: 'fast',
                sustain: 'short',
                spectral_role: 'full'
            }
        },
        mixTraits: {
            eq: { lowCut: 30, highCut: 16000, cuts: [{ freq: 400, gain: -3 }] },
            compression: { threshold: -20, ratio: 4, attack: 0.01, release: 0.1 },
            pan: { strategy: 'width', amount: 0.8 }, // Drums often wide
            sends: { reverb: 0.1, delay: 0.05 }
        },
        traits: { family: 'percussion', section: 'low', orchestral: false },
        tags: ['rock', 'band', 'pop', 'jazz', 'electronic', 'studio'],
        baselineGain: -10 // Lowered for Mix Bus (-10dB)
    },
    {
        id: 'lead_guitar',
        name: 'Lead Guitar',
        tags: ['rock', 'band', 'metal', 'blues', 'indie'],
        category: 'Band',
        type: 'monophonic',
        range: RANGES.Guitar,
        roles: ['melody', 'solo'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.LEAD_GUITAR,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'infinite',
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 80,
        saturation_affinity: 0.9,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        logicTags: { ...STRING_LOGIC_TAGS },
        capabilities: {
            articulations: ['long', 'short', 'palm_mute'],
            requiresAmp: true,
            useDynamicsCC: false,
            traits: {
                timbre: 'bright',
                attack: 'fast',
                sustain: 'long',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 100, boost: [{ freq: 2500, gain: 3 }] },
            pan: { fixed: 0.2 }, // Slightly right
            sends: { delay: 0.2, reverb: 0.1 },
            distortion: 0.8 // Explicit override for SynthFactory
        },
        baselineGain: -12, // Lowered for Mix Bus (-12dB)
        traits: { family: 'plucked', section: 'mid', orchestral: false, agility: 'high' }
    },
    {
        id: 'rhythm_guitar',
        name: 'Rhythm Guitar',
        tags: ['rock', 'band', 'indie', 'punk'],
        category: 'Band',
        type: 'polyphonic',
        range: RANGES.Guitar,
        roles: ['harmony', 'rhythm'],
        midiChannel: 2,
        rank: INSTRUMENT_HIERARCHY.RHYTHM_GUITAR,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'loose',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 2,
        spectral_floor: 80,
        saturation_affinity: 0.8,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        logicTags: { ...STRING_LOGIC_TAGS },
        capabilities: {
            articulations: ['power_chord', 'strum', 'mute'],
            requiresAmp: true,
            useDynamicsCC: false,
            traits: {
                timbre: 'warm',
                attack: 'medium',
                sustain: 'medium',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 100, cuts: [{ freq: 400, gain: -2 }] },
            pan: { fixed: -0.2 }, // Slightly left
            sends: { reverb: 0.1 }
        },
        baselineGain: -12, // Lowered for Mix Bus
        traits: { family: 'plucked', section: 'mid', orchestral: false }
    },
    {
        id: 'electric_bass',
        name: 'Electric Bass',
        tags: ['rock', 'band', 'funk', 'pop', 'metal'],
        category: 'Band',
        type: 'monophonic',
        range: RANGES.Bass,
        roles: ['bass'],
        midiChannel: 7,
        rank: INSTRUMENT_HIERARCHY.BASS,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 2,
        spectral_floor: 40,
        saturation_affinity: 0.7,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        logicTags: { ...STRING_LOGIC_TAGS },
        capabilities: {
            articulations: ['pluck', 'slap', 'pick'],
            useDynamicsCC: false,
            traits: {
                timbre: 'dark',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'low'
            }
        },
        mixTraits: {
            eq: { lowCut: 30, boost: [{ freq: 80, gain: 2 }] },
            compression: { threshold: -15, ratio: 3, attack: 0.01, release: 0.1 },
            pan: { fixed: 0.0 }
        },
        baselineGain: -10, // Lowered for Mix Bus
        traits: { family: 'plucked', section: 'low', orchestral: false }
    },
    {
        id: 'piano',
        name: 'Piano',
        tags: ['keys', 'jazz', 'classical', 'pop', 'ballad'],
        category: 'Band',
        baselineGain: -8, // Boosted for Presence
        type: 'polyphonic',
        range: RANGES.Piano,
        roles: ['melody', 'harmony', 'rhythm'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.PIANO_RH,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 3,
        spectral_floor: 60,
        saturation_affinity: 0.2,

        // Operation Virtuoso (Rhodes Piano Simulation)
        dynamic_profile: 'expressive',
        envelope_bias: 'lazy',
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['sustain', 'staccato'],
            pedal: true,
            useDynamicsCC: false,
            traits: {
                timbre: 'bright',
                attack: 'fast',
                sustain: 'long',
                spectral_role: 'full'
            }
        },
        mixTraits: {
            eq: { lowCut: 50, boost: [{ freq: 5000, gain: 2 }] },
            pan: { width: 0.8 },
            sends: { reverb: 0.15 }
        },
        traits: { family: 'keys', section: 'full', orchestral: false }
    },
    {
        id: 'harpsichord',
        name: 'Harpsichord',
        tags: ['keys', 'baroque', 'classical'],
        category: 'Band',
        baselineGain: -6, // Plucky and sharp
        type: 'polyphonic',
        range: RANGES.Harpsichord,
        roles: ['bass', 'harmony', 'rhythm', 'counter_melody'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.PIANO_RH,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'mechanical', // No velocity sensitivity usually
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 2,
        spectral_floor: 100,
        saturation_affinity: 0.3,
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['pluck'],
            useDynamicsCC: false, // Velocity fixed often
            traits: {
                timbre: 'bright',
                attack: 'fast',
                sustain: 'short',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 80, boost: [{ freq: 4000, gain: 4 }] }, // Crisp
            pan: { width: 0.7 },
            sends: { reverb: 0.2 }
        },
        traits: { family: 'keys', section: 'mid', orchestral: true }
    },

    // --- ORCHESTRAL STRINGS ---
    {
        id: 'violin_1',
        name: 'Violins 1',
        tags: ['orchestral', 'strings', 'classical', 'cinematic'],
        baselineGain: -8, // Boosted for Presence
        mixTraits: {
            eq: { lowCut: 150, boost: [{ freq: 4000, gain: 2 }] },
            pan: { fixed: -0.4 }, // Standard orchestral seating (Left)
            sends: { reverb: 0.3 } // Strings need wash
        },
        preferredEngine: 'sampler',
        sampleMap: {
            default: {
                'A3': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/A3.mp3',
                'A4': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/A4.mp3',
                'A5': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/A5.mp3',
                'A6': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/A6.mp3',
                'C4': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/C4.mp3',
                'C5': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/C5.mp3',
                'C6': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/C6.mp3',
                'C7': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/C7.mp3',
                'E4': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/E4.mp3',
                'E5': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/E5.mp3',
                'E6': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/E6.mp3',
                'G4': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/G4.mp3',
                'G5': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/G5.mp3',
                'G6': 'https://raw.githubusercontent.com/nbrosowsky/tonejs-instruments/master/samples/violin/G6.mp3'
            }
        },
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Violin,
        roles: ['melody', 'counter_melody'],
        midiChannel: 3,
        rank: INSTRUMENT_HIERARCHY.VIOLIN_1,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'infinite',
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 180,
        saturation_affinity: 0.4,
        tessitura: { low: 67, high: 84, preferred_center: 76 },
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationSet: 'discover', // Default logic profile (swappable to 'pro')
        articulationMap: STRING_LOGIC_TAGS, // Explicit Map
        logicTags: STRING_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'spiccato', 'pizzicato', 'tremolo'],
            useDynamicsCC: true,
            traits: {
                timbre: 'bright',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'high'
            }
        },
        traits: { family: 'strings', section: 'high', orchestral: true, agility: 'high' }
    },
    {
        id: 'violin_2',
        name: 'Violins 2',
        tags: ['orchestral', 'strings', 'classical', 'cinematic'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Violin,
        roles: ['harmony', 'counter_melody'],
        midiChannel: 4,
        rank: INSTRUMENT_HIERARCHY.VIOLIN_2,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 3,
        spectral_floor: 180,
        saturation_affinity: 0.3,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: STRING_LOGIC_TAGS,
        logicTags: STRING_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'spiccato', 'pizzicato', 'tremolo'],
            useDynamicsCC: true,
            traits: {
                timbre: 'warm',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'high'
            }
        },
        mixTraits: {
            eq: { lowCut: 150 },
            pan: { fixed: -0.25 }, // Mid-Left
            sends: { reverb: 0.3 }
        },
        traits: { family: 'strings', section: 'high', orchestral: true, agility: 'high' }
    },
    {
        id: 'viola',
        name: 'Violas',
        tags: ['orchestral', 'strings', 'classical', 'cinematic'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Viola,
        roles: ['harmony', 'counter_melody'],
        midiChannel: 5,
        rank: INSTRUMENT_HIERARCHY.VIOLA,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 3,
        spectral_floor: 130,
        saturation_affinity: 0.3,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: STRING_LOGIC_TAGS,
        logicTags: STRING_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'spiccato', 'pizzicato', 'tremolo'],
            useDynamicsCC: true,
            traits: {
                timbre: 'warm',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 120 },
            pan: { fixed: 0.0 }, // Center
            sends: { reverb: 0.3 }
        },
        traits: { family: 'strings', section: 'mid', orchestral: true }
    },
    {
        id: 'cello',
        name: 'Cellos',
        tags: ['orchestral', 'strings', 'classical', 'cinematic', 'chamber'],
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Cello,
        roles: ['bass', 'harmony', 'counter_melody'],
        midiChannel: 6,
        rank: INSTRUMENT_HIERARCHY.CELLO,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'infinite',
        conversation_mode: 'listen',
        mix_priority: 2,
        spectral_floor: 60,
        saturation_affinity: 0.4,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: STRING_LOGIC_TAGS,
        logicTags: STRING_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'spiccato', 'pizzicato', 'tremolo'],
            useDynamicsCC: true,
            traits: {
                timbre: 'warm',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'low'
            }
        },
        mixTraits: {
            eq: { lowCut: 60 },
            pan: { fixed: 0.3 }, // Mid-Right
            sends: { reverb: 0.25 },
            gain: 3 // Boost Ghost Instrument
        },
        baselineGain: -5, // Boosted for Presence (-8 + 3)
        traits: { family: 'strings', section: 'low', orchestral: true }
    },
    {
        id: 'contrabass',
        name: 'Basses (Orchestral)',
        tags: ['orchestral', 'strings', 'classical', 'cinematic'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Contrabass,
        roles: ['bass'],
        midiChannel: 7,
        rank: INSTRUMENT_HIERARCHY.BASS,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 2,
        spectral_floor: 30,
        saturation_affinity: 0.5,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: STRING_LOGIC_TAGS,
        logicTags: STRING_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'spiccato', 'pizzicato'],
            useDynamicsCC: true,
            traits: {
                timbre: 'dark',
                attack: 'slow',
                sustain: 'long',
                spectral_role: 'low'
            }
        },
        mixTraits: {
            eq: { lowCut: 30, highCut: 8000 },
            pan: { fixed: 0.45 }, // Right
            sends: { reverb: 0.35 } // Big reverb for bass sections
        },
        traits: { family: 'strings', section: 'low', orchestral: true }
    },

    // --- ORCHESTRAL WINDS ---
    {
        id: 'flute',
        name: 'Flute',
        tags: ['orchestral', 'woodwinds', 'classical'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Flute,
        roles: ['melody', 'counter_melody'],
        midiChannel: 8,
        rank: INSTRUMENT_HIERARCHY.FLUTE,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'shallow', // 1 bar
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 250,
        saturation_affinity: 0.0,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: WIND_LOGIC_TAGS,
        logicTags: WIND_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'staccato'],
            useDynamicsCC: true,
            traits: {
                timbre: 'bright',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'high'
            }
        },
        mixTraits: {
            eq: { lowCut: 200, boost: [{ freq: 5000, gain: 2 }] },
            pan: { fixed: -0.1 }, // Center-Left
            sends: { reverb: 0.25 }
        },
        traits: { family: 'woodwinds', section: 'high', orchestral: true, agility: 'high' }
    },
    {
        id: 'oboe',
        name: 'Oboe',
        tags: ['orchestral', 'woodwinds', 'classical'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Oboe,
        roles: ['melody', 'counter_melody'],
        midiChannel: 9,
        rank: INSTRUMENT_HIERARCHY.OBOE,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'average', // 2 bars
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 200,
        saturation_affinity: 0.1,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: WIND_LOGIC_TAGS,
        logicTags: WIND_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'staccato'],
            useDynamicsCC: true,
            traits: {
                timbre: 'bright',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'high'
            }
        },
        mixTraits: {
            eq: { lowCut: 180, cuts: [{ freq: 1000, gain: -2 }] }, // Cut nasal freq
            pan: { fixed: 0.1 }, // Center-Right
            sends: { reverb: 0.25 }
        },
        traits: { family: 'woodwinds', section: 'high', orchestral: true }
    },
    {
        id: 'clarinet',
        name: 'Clarinet',
        tags: ['orchestral', 'klezmer', 'balkan'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Clarinet,
        roles: ['melody', 'harmony'],
        midiChannel: 10,
        rank: INSTRUMENT_HIERARCHY.CLARINET,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'average',
        conversation_mode: 'listen',
        mix_priority: 2,
        spectral_floor: 140,
        saturation_affinity: 0.1,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: WIND_LOGIC_TAGS,
        logicTags: WIND_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'staccato'],
            useDynamicsCC: true,
            traits: {
                timbre: 'warm',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 140 },
            pan: { fixed: -0.15 }, // Center-Left
            sends: { reverb: 0.2 }
        },
        traits: { family: 'woodwinds', section: 'mid', orchestral: true }
    },
    {
        id: 'bassoon',
        name: 'Bassoon',
        tags: ['orchestral', 'woodwinds', 'classical'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Bassoon,
        roles: ['bass', 'harmony'],
        midiChannel: 11,
        rank: INSTRUMENT_HIERARCHY.BASSOON,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'average',
        conversation_mode: 'sustain',
        mix_priority: 3,
        spectral_floor: 60,
        saturation_affinity: 0.2,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: WIND_LOGIC_TAGS,
        logicTags: WIND_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'staccato'],
            useDynamicsCC: true,
            traits: {
                timbre: 'wood',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'low'
            }
        },
        mixTraits: {
            eq: { lowCut: 60 },
            pan: { fixed: 0.15 }, // Center-Right
            sends: { reverb: 0.2 }
        },
        traits: { family: 'woodwinds', section: 'low', orchestral: true }
    },

    // --- ORCHESTRAL BRASS ---
    {
        id: 'saxophone',
        name: 'Saxophone',
        tags: ['jazz', 'band', 'pop', 'woodwind'],
        baselineGain: -8,
        category: 'Band',
        type: 'monophonic',
        range: { min: 48, max: 80 }, // Tenor Sax approx
        roles: ['melody', 'solo', 'counter_melody'],
        midiChannel: 12,
        rank: INSTRUMENT_HIERARCHY.SAXOPHONE,

        humanize_profile: 'expressive',
        lung_capacity: 'athletic',
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 100,
        saturation_affinity: 0.6,

        keyswitches: {},
        articulationMap: WIND_LOGIC_TAGS,
        logicTags: WIND_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'staccato', 'growl'],
            useDynamicsCC: true,
            traits: {
                timbre: 'bright',
                attack: 'fast',
                sustain: 'long',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 100, boost: [{ freq: 2500, gain: 3 }] },
            pan: { fixed: 0.0 },
            sends: { reverb: 0.3 }
        },
        traits: { family: 'woodwinds', section: 'mid', orchestral: false, agility: 'high' }
    },
    {
        id: 'trumpet',
        name: 'Trumpet',
        tags: ['orchestral', 'brass', 'jazz', 'mariachi', 'fanfare'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Trumpet,
        roles: ['melody', 'fanfare'],
        midiChannel: 12,
        rank: INSTRUMENT_HIERARCHY.TRUMPET,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'athletic', // 4 bars
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 150,
        saturation_affinity: 0.5,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: WIND_LOGIC_TAGS,
        logicTags: WIND_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'staccato', 'marcato'],
            useDynamicsCC: true,
            traits: {
                timbre: 'bright',
                attack: 'fast',
                sustain: 'long',
                spectral_role: 'high'
            }
        },
        mixTraits: {
            eq: { lowCut: 150, boost: [{ freq: 3000, gain: 2 }] },
            pan: { fixed: 0.2 }, // Back Rightish
            sends: { reverb: 0.35 } // Brass loves reverb
        },
        traits: { family: 'brass', section: 'high', orchestral: true, legatoType: 'slurred' }
    },
    {
        id: 'horn',
        name: 'French Horn',
        tags: ['orchestral', 'brass', 'cinematic'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Horn,
        roles: ['harmony', 'counter_melody'],
        midiChannel: 13,
        rank: INSTRUMENT_HIERARCHY.HORN,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'athletic',
        conversation_mode: 'sustain',
        mix_priority: 3,
        spectral_floor: 80,
        saturation_affinity: 0.4,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: WIND_LOGIC_TAGS,
        logicTags: WIND_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'staccato', 'marcato'],
            useDynamicsCC: true,
            traits: {
                timbre: 'warm',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 80, cuts: [{ freq: 400, gain: -2 }] }, // Clean mud
            pan: { fixed: -0.3 }, // Back Left
            sends: { reverb: 0.4 } // Very wet
        },
        traits: { family: 'brass', section: 'mid', orchestral: true }
    },
    {
        id: 'trombone',
        name: 'Trombone',
        tags: ['orchestral', 'brass', 'jazz', 'big_band'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Trombone,
        roles: ['bass', 'harmony'],
        midiChannel: 14,
        rank: INSTRUMENT_HIERARCHY.TROMBONE,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'athletic',
        conversation_mode: 'sustain',
        mix_priority: 3,
        spectral_floor: 60,
        saturation_affinity: 0.6,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: WIND_LOGIC_TAGS,
        logicTags: WIND_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'staccato', 'marcato'],
            useDynamicsCC: true,
            traits: {
                timbre: 'bright',
                attack: 'medium',
                sustain: 'long',
                spectral_role: 'low'
            }
        },
        mixTraits: {
            eq: { lowCut: 60 },
            pan: { fixed: 0.3 }, // Back Right
            sends: { reverb: 0.3 }
        },
        traits: { family: 'brass', section: 'low', orchestral: true }
    },
    {
        id: 'tuba',
        name: 'Tuba',
        tags: ['western', 'polka', 'brass'],
        baselineGain: -8,
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Tuba,
        roles: ['bass'],
        midiChannel: 15,
        rank: INSTRUMENT_HIERARCHY.TUBA,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'athletic',
        conversation_mode: 'sustain',
        mix_priority: 2,
        spectral_floor: 30,
        saturation_affinity: 0.4,
        // -----------------------------

        keyswitches: BBCO_KEYSWITCHES,
        articulationMap: WIND_LOGIC_TAGS,
        logicTags: WIND_LOGIC_TAGS,
        capabilities: {
            articulations: ['legato', 'staccato'],
            useDynamicsCC: true,
            traits: {
                timbre: 'dark',
                attack: 'slow',
                sustain: 'long',
                spectral_role: 'low'
            }
        },
        mixTraits: {
            eq: { lowCut: 20 },
            pan: { fixed: 0.4 }, // Back Right
            sends: { reverb: 0.3 }
        },
        traits: { family: 'brass', section: 'low', orchestral: true }
    },
    {
        id: 'timpani',
        name: 'Timpani',
        tags: ['orchestral', 'percussion', 'cinematic'],
        category: 'Orchestral',
        type: 'monophonic',
        range: RANGES.Timpani,
        roles: ['percussion', 'bass'],
        midiChannel: 16,
        rank: INSTRUMENT_HIERARCHY.PERCUSSION,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'infinite',
        conversation_mode: 'listen',
        mix_priority: 2,
        spectral_floor: 40,
        saturation_affinity: 0.5,
        // -----------------------------

        keyswitches: { HIT: 0, ROLL: 1 },
        articulationMap: { hit: 0, roll: 1 },
        logicTags: { hit: 0, roll: 1 },
        capabilities: {
            articulations: ['hit', 'roll'],
            useDynamicsCC: true,
            traits: {
                timbre: 'dark',
                attack: 'fast',
                sustain: 'medium',
                spectral_role: 'low'
            }
        },
        mixTraits: {
            eq: { lowCut: 40, boost: [{ freq: 100, gain: 3 }] },
            pan: { fixed: 0.0 }, // Center Back
            sends: { reverb: 0.4 }
        },
        traits: { family: 'percussion', section: 'low', orchestral: true }
    },
    {
        id: 'harp',
        name: 'Harp',
        tags: ['orchestral', 'plucked', 'fantasy'],
        category: 'Orchestral',
        type: 'polyphonic',
        range: RANGES.Harp, // Use new Range C3-C7
        roles: ['harmony', 'decoration'],
        midiChannel: 15, // Shared or unique
        rank: 90,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'loose',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 3,
        spectral_floor: 50,
        saturation_affinity: 0.0,
        // -----------------------------

        preferredEngine: 'sampler',
        sampleMap: {
            default: {
                'C2': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/C2.mp3',
                'E2': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/E2.mp3',
                'G2': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/G2.mp3',
                'A2': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/A2.mp3',
                'C3': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/C3.mp3',
                'E3': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/E3.mp3',
                'G3': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/G3.mp3',
                'A3': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/A3.mp3',
                'C4': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/C4.mp3',
                'E4': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/E4.mp3',
                'G4': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/G4.mp3',
                'A4': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/A4.mp3',
                'C5': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/C5.mp3',
                'E5': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/E5.mp3',
                'G5': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/G5.mp3',
                'A5': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/A5.mp3',
                'C6': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/C6.mp3',
                'E6': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/E6.mp3',
                'G6': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/G6.mp3',
                'A6': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/A6.mp3',
                'C7': 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/orchestral_harp-mp3/C7.mp3'
            }
        },

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['pluck'],
            useDynamicsCC: false,
            traits: {
                timbre: 'bright',
                attack: 'fast',
                sustain: 'medium',
                spectral_role: 'full'
            }
        },
        mixTraits: {
            eq: { lowCut: 40, boost: [{ freq: 8000, gain: 3 }] }, // Sparkle
            pan: { width: 0.9 }, // Wide
            sends: { reverb: 0.3 },
            gain: 4 // Boost Ghost Instrument
        },
        baselineGain: -4, // Significant Boost (-8 to -4)
        traits: { family: 'plucked', section: 'full', orchestral: true }
    },
    {
        id: 'orchestral_percussion',
        name: 'Orch. Percussion',
        tags: ['orchestral', 'percussion', 'cinematic'],
        category: 'Orchestral',
        type: 'drum',
        range: { min: 0, max: 127 },
        roles: ['percussion'],
        midiChannel: 16, // Often shared with Timpani or separate
        rank: INSTRUMENT_HIERARCHY.PERCUSSION,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'tight',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 4, // Expendable
        spectral_floor: 100,
        saturation_affinity: 0.5,
        // -----------------------------

        keyswitches: {}, // Mapped by note
        logicTags: {},
        capabilities: {
            articulations: ['hit'],
            useDynamicsCC: true,
            traits: {
                timbre: 'neutral',
                attack: 'fast',
                sustain: 'short',
                spectral_role: 'full'
            }
        },
        mixTraits: {
            eq: { lowCut: 30, highCut: 16000 },
            pan: { width: 1.0 }, // Full width
            sends: { reverb: 0.35 }
        },
        traits: { family: 'percussion', section: 'full', orchestral: true }
    },
    {
        id: 'marimba',
        name: 'Marimba',
        tags: ['orchestral', 'percussion', 'mallet', 'minimalist'],
        category: 'Orchestral',
        type: 'polyphonic',
        range: { min: 45, max: 96 }, // A2 - C7
        roles: ['melody', 'harmony', 'rhythm', 'ostinato'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.PERCUSSION,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'mechanical', // Consistent pulse
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 2,
        spectral_floor: 100,
        saturation_affinity: 0.1,
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['hit', 'roll'],
            useDynamicsCC: false, // Velocity based
            traits: {
                timbre: 'wood',
                attack: 'fast',
                sustain: 'short',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 80, boost: [{ freq: 2000, gain: 3 }] },
            pan: { width: 0.6 },
            sends: { reverb: 0.2 }
        },
        baselineGain: -6,
        traits: { family: 'percussion', section: 'mid', orchestral: true }
    },

    // --- EDM / SYNTH ---
    {
        id: 'supersaw_lead',
        name: 'Supersaw Lead',
        tags: ['electronic', 'synth', 'edm', 'techno'],
        baselineGain: -6, // Boosted (was -14)
        category: 'EDM',
        type: 'polyphonic',
        range: RANGES.SynthLead,
        roles: ['lead', 'melody'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.SYNTH_LEAD,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'locked',
        lung_capacity: 'infinite',
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 150,
        saturation_affinity: 0.9,
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['sustain'],
            useDynamicsCC: true,
            traits: {
                timbre: 'bright',
                attack: 'fast',
                sustain: 'infinite',
                spectral_role: 'high'
            }
        },
        mixTraits: {
            eq: { lowCut: 150, boost: [{ freq: 5000, gain: 4 }] },
            pan: { width: 0.8 },
            sends: { delay: 0.3, reverb: 0.4 }
        },
        traits: { family: 'synth', section: 'high', orchestral: false }
    },
    {
        id: 'bass_drop',
        name: 'Bass Drop FX',
        tags: ['electronic', 'fx', 'dubstep', 'trap'],
        category: 'EDM',
        baselineGain: -4, // Boosted
        type: 'monophonic',
        range: RANGES.SubBass,
        roles: ['fx', 'bass'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.BASS,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'locked',
        lung_capacity: 'infinite',
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 10,
        saturation_affinity: 1.0,
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['drop'],
            useDynamicsCC: false,
            traits: {
                timbre: 'dark',
                attack: 'slow',
                sustain: 'long',
                spectral_role: 'low'
            }
        },
        mixTraits: {
            eq: { lowCut: 10, highCut: 200 }, // Sub only
            compression: { threshold: -5, ratio: 10, attack: 0.01, release: 0.5 },
            pan: { fixed: 0.0 }
        },
        traits: { family: 'synth', section: 'low', orchestral: false }
    },
    {
        id: 'pluck_synth',
        name: 'Pluck Synth',
        tags: ['electronic', 'synth', 'pop'],
        category: 'EDM',
        baselineGain: -8, // Standard level
        type: 'polyphonic',
        range: RANGES.Piano,
        roles: ['harmony', 'arp'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.PIANO_RH,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'locked',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 3,
        spectral_floor: 150,
        saturation_affinity: 0.6,
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['pluck'],
            useDynamicsCC: true,
            traits: {
                timbre: 'bright',
                attack: 'fast',
                sustain: 'short',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 150 },
            pan: { width: 0.7 },
            sends: { delay: 0.4, reverb: 0.2 }
        },
        traits: { family: 'synth', section: 'mid', orchestral: false }
    },
    {
        id: 'glass_pad',
        name: 'Glass Pad',
        tags: ['electronic', 'pad', 'ambient'],
        category: 'EDM',
        type: 'additive_pad',
        range: RANGES.Piano,
        roles: ['harmony', 'pad', 'atmosphere'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.PIANO_RH,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'locked',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 3,
        spectral_floor: 200,
        saturation_affinity: 0.4,
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['sustain'],
            useDynamicsCC: true,
            traits: {
                timbre: 'bright',
                attack: 'slow',
                sustain: 'infinite',
                spectral_role: 'full'
            }
        },
        mixTraits: {
            eq: { lowCut: 200, highCut: 10000 },
            pan: { width: 0.9 },
            sends: { reverb: 0.5 }
        },
        traits: { family: 'synth', section: 'full', orchestral: false }
    },
    {
        id: 'pad',
        name: 'Warm Pad',
        tags: ['electronic', 'pad', 'ambient', 'sci-fi'],
        category: 'EDM',
        type: 'polyphonic',
        range: { min: 36, max: 84 },
        roles: ['harmony', 'pad', 'bass'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.PIANO_RH,
        humanize_profile: 'locked',
        lung_capacity: 'infinite',
        conversation_mode: 'sustain',
        mix_priority: 2,
        spectral_floor: 60,
        saturation_affinity: 0.8,
        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['sustain'],
            useDynamicsCC: true,
            traits: {
                timbre: 'warm',
                attack: 'slow',
                sustain: 'infinite',
                spectral_role: 'low' // Fills the low-mid gap
            }
        },
        mixTraits: {
            eq: { lowCut: 60, highCut: 6000 },
            pan: { width: 0.8 },
            sends: { reverb: 0.4 }
        },
        traits: { family: 'synth', section: 'mid', orchestral: false }
    },

    // --- WORLD ---
    {
        id: 'sitar',
        name: 'Sitar',
        tags: ['indian', 'plucked', 'drone'],
        category: 'World',
        type: 'monophonic',
        range: RANGES.Sitar,
        roles: ['melody', 'drone'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.LEAD_GUITAR,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'infinite',
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 100,
        saturation_affinity: 0.7,
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['pluck', 'bend'],
            useDynamicsCC: true,
            traits: {
                timbre: 'metallic',
                attack: 'fast',
                sustain: 'medium',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 100, boost: [{ freq: 3000, gain: 3 }] }, // Resonance
            pan: { fixed: 0.2 },
            sends: { reverb: 0.2 }
        },
        traits: { family: 'plucked', section: 'mid', orchestral: false }
    },
    {
        id: 'koto',
        name: 'Koto',
        tags: ['east-asian', 'plucked'],
        category: 'World',
        type: 'polyphonic',
        range: RANGES.Koto,
        roles: ['melody', 'harmony'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.LEAD_GUITAR,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'infinite',
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 100,
        saturation_affinity: 0.5,
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['pluck'],
            useDynamicsCC: true,
            traits: {
                timbre: 'wood',
                attack: 'fast',
                sustain: 'short',
                spectral_role: 'mid'
            }
        },
        mixTraits: {
            eq: { lowCut: 100, boost: [{ freq: 4000, gain: 2 }] },
            pan: { fixed: -0.2 },
            sends: { reverb: 0.15 }
        },
        traits: { family: 'plucked', section: 'mid', orchestral: false }
    },
    {
        id: 'duduk',
        name: 'Duduk',
        tags: ['middle-eastern', 'wind', 'balkan'],
        category: 'World',
        type: 'monophonic',
        range: RANGES.Duduk,
        roles: ['melody'],
        midiChannel: 1,
        rank: INSTRUMENT_HIERARCHY.FLUTE,

        // --- DATA INJECTION UPGRADE ---
        humanize_profile: 'expressive',
        lung_capacity: 'average',
        conversation_mode: 'listen',
        mix_priority: 1,
        spectral_floor: 150,
        saturation_affinity: 0.3,
        // -----------------------------

        keyswitches: {},
        logicTags: {},
        capabilities: {
            articulations: ['legato', 'vib'],
            useDynamicsCC: true,
            traits: {
                timbre: 'wood',
                attack: 'slow',
                sustain: 'long',
                spectral_role: 'high'
            }
        },
        mixTraits: {
            eq: { lowCut: 150, boost: [{ freq: 1000, gain: 2 }] },
            pan: { fixed: 0.0 },
            sends: { reverb: 0.35, delay: 0.1 }
        },
        traits: { family: 'woodwinds', section: 'high', orchestral: false }
    }
];

export const getInstrumentById = (id) => INSTRUMENT_LIBRARY.find(inst => inst.id === id);
